import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FaUser, FaUsers, FaGamepad, FaCrown } from "react-icons/fa";
import { useSubscription } from "../contexts/SubscriptionContext";
import { Button } from "../ui";
import Logo from "./site/Logo";
import SiteBackdrop from "./site/SiteBackdrop";
import DiscountBanner from "./site/DiscountBanner";
import BetaNotice from "./BetaNotice";

const NAV = [
  { label: "ألعابي", to: "/app/games" },
  { label: "المجتمع", to: "/app/community" },
  { label: "حسابي", to: "/app/profile" },
];

// "/app" (the index route) shows the games list too.
const useGamesActive = () => {
  const { pathname } = useLocation();
  return pathname === "/app" || pathname === "/app/";
};

const navClass = (isActive) =>
  `px-3 py-2 rounded-lg text-[15px] font-semibold transition-colors ${
    isActive ? "text-white bg-white/10" : "text-white/70 hover:text-white hover:bg-white/5"
  }`;

// Sticky top bar, same look as the public SiteHeader: logo home, the app
// sections on desktop, and the premium status on the left.
const TopBar = () => {
  const { isPremium } = useSubscription();
  const gamesActive = useGamesActive();
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-header/85 backdrop-blur-md">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center gap-3">
        <Logo to="/" className="w-11 h-11" />

        <nav className="hidden md:flex items-center gap-1 mr-4" aria-label="أقسام التطبيق">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                navClass(isActive || (item.to === "/app/games" && gamesActive))
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1" />

        {isPremium ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 border border-primary/30 px-3 h-9 text-sm font-bold text-primary">
            <FaCrown aria-hidden="true" /> مميز
          </span>
        ) : (
          <Button
            to="/pricing"
            variant="ghost"
            size="sm"
            icon={<FaCrown className="text-primary" aria-hidden="true" />}
          >
            اشترك
          </Button>
        )}
      </div>
    </header>
  );
};

// Bottom tab bar matching the mobile app's GameTabBar: icon + label tabs with a
// raised copper game controller in the center. Phones and tablets only.
const tabClass = ({ isActive }) =>
  `flex flex-col items-center justify-center gap-1 flex-1 py-1 transition-colors ${
    isActive ? "text-primary" : "text-white/55 hover:text-white"
  }`;

const BottomBar = () => {
  const gamesActive = useGamesActive();
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 flex justify-center"
      aria-label="التنقل"
    >
      <div
        className="w-full max-w-md bg-header/95 backdrop-blur-md rounded-t-3xl border-t border-x border-white/10 shadow-[0_-12px_30px_-12px_rgb(0_0_0/0.5)] flex items-end justify-evenly px-3 pt-2"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        <NavLink to="/app/community" className={tabClass}>
          <FaUsers size={22} aria-hidden="true" />
          <span className="text-[11px] font-semibold">المجتمع</span>
        </NavLink>

        <NavLink
          to="/app/games"
          className="flex flex-col items-center -mt-7"
          aria-label="ألعابي"
        >
          {({ isActive }) => (
            <span
              className={`w-20 h-20 rounded-2xl flex items-center justify-center border-2 shadow-[0_10px_24px_-10px_rgb(var(--c-mainButton)/0.8)] transition-colors ${
                isActive || gamesActive
                  ? "bg-mainButtonShadow border-primary"
                  : "bg-mainButton border-primary/50"
              }`}
            >
              <FaGamepad className="text-white" size={36} aria-hidden="true" />
            </span>
          )}
        </NavLink>

        <NavLink to="/app/profile" className={tabClass}>
          <FaUser size={22} aria-hidden="true" />
          <span className="text-[11px] font-semibold">حسابي</span>
        </NavLink>
      </div>
    </nav>
  );
};

const AppShell = () => (
  <div
    dir="rtl"
    className="relative isolate min-h-screen overflow-x-clip bg-header font-sans text-white"
  >
    <SiteBackdrop />
    <TopBar />
    <BetaNotice />
    <DiscountBanner />
    <main className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-6 md:pt-10 pb-28 md:pb-12">
      <Outlet />
    </main>
    <BottomBar />
  </div>
);

export default AppShell;
